'use client';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useWindowSize } from '@uidotdev/usehooks';
import DragElements from '@/components/fancy/drag-elements';
import { Card } from '@/components/ui/homecards';
import { BookOpenCheck, Zap, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import { Testimonials } from '@/components/ui/testimonials';
import Footer from '@/components/nav/footer';
import { PWAInstallPrompt } from './InstallPrompt';

const urls = [
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGzwibMVNTPvJGLabWrUKf18Qwgq5MmXkBxRlo6',
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGznKTIUNUupi9SzdjPQgOXbmwVrfFHWe0CcTvB',
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGz0jHDom47mzbT6xwQPgXdNWj12tlG35CZUsnL',
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGz2UPhnQjj35unPXRrlcUF1xZW2bkBmvhfDIEa',
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGzudXIzRaGsDLYiqPBd3jwRofcFtIzbKNrXlZS',
	'https://beuw6r12zz.ufs.sh/f/cEyWJRCaFWGzcIgKXcCaFWGzj3Ow0ry9Q6bnZtSYVxqhDAeg',
];

const cardContent = {
	title: 'Verified & Trusted',
	description:
		'Every Pass you purchase is verified for authenticity, giving you peace of mind. Say goodbye to scams and hello to secure access to your favorite events!',
};

const cardContent1 = {
	title: 'Effortless and Seamless',
	description:
		"From browsing to checkout, we've designed every step to be easy, fast, and frustration-free. Your next unforgettable experience is just a few taps away!",
};

const cardContent2 = {
	title: 'The Best Events, All in One Place',
	description:
		"Discover concerts, festivals, and experiences you'll love. We bring all your favorite events together so you never miss a moment!",
};

const randomInt = (min: number, max: number) => {
	return Math.floor(Math.random() * (max - min + 1)) + min;
};

const Landing: React.FC = () => {
	const screenSize = useWindowSize();
	const [randomStyles, setRandomStyles] = useState<
		{ rotation: number; width: number; height: number }[]
	>([]);
	const router = useRouter();
	// Generate random styles after hydration
	useEffect(() => {
		const styles = urls.map(() => {
			const rotation = randomInt(-12, 12);
			const width =
				(screenSize.width ?? 0) < 768
					? randomInt(180, 200) // Mobile: Slightly larger
					: randomInt(200, 240); // Desktop: Larger range
			const height =
				(screenSize.width ?? 0) < 768
					? randomInt(200, 220)
					: randomInt(240, 280);
			return { rotation, width, height };
		});
		setRandomStyles(styles);
	}, [screenSize.width]);

	return (
		<main className='w-screen h-full relative bg-background'>
			<div className='w-full flex justify-center items-center z-50'>
				<PWAInstallPrompt />
			</div>

			{/* Colorful background elements */}
			<div className='fixed inset-0 pointer-events-none z-0'>
				<div className='absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl' />
				<div className='absolute top-3/4 right-1/4 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl' />
				<div className='absolute bottom-1/4 left-1/3 w-72 h-72 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 rounded-full blur-3xl' />
			</div>

			{/* Hero Section */}
			<section className='w-screen h-screen relative bg-gradient-to-br from-background via-background to-purple-50/30 dark:to-purple-950/20 scroll-snap-start'>
				<h1 className='absolute text-2xl md:text-6xl md:ml-36 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-muted-foreground uppercase w-full'>
					Your{' '}
					<span className='font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'>
						pass
					</span>
					, Your
					<span className='font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent'>
						{' '}
						story{' '}
					</span>
				</h1>
				<p className='absolute text-xs md:text-sm md:ml-36 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-muted-foreground w-full mt-10'>
					A vibe, verfied, and vetted
				</p>
				<DragElements dragMomentum={true} className='p-10'>
					{urls.map((url, index) => {
						const style = randomStyles[index];
						if (!style) return null; // Wait for styles to generate
						return (
							<div
								key={index}
								className={`flex flex-col items-center justify-start bg-white dark:bg-zinc-900 shadow-2xl relative group transition-all duration-300`}
								style={{
									transform: `rotate(${style.rotation}deg)`,
									width: `${style.width}px`,
									height: `${style.height}px`,
								}}
							>
								{/* Lanyard */}
								<div
									className='absolute -top-[100px] left-1/2 w-20 -translate-x-1/2 z-[-1]'
									style={{
										height: '100px',
										background: 'rgb(var(--foreground) / 0.6)',
										clipPath: 'polygon(30% 0, 70% 0, 60% 100%, 40% 100%)',
									}}
								/>

								{/* Lanyard hole */}
								<div className='absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-4 bg-background dark:bg-zinc-800 rounded-b-full border-2 border-t-0 border-foreground/20 dark:border-white/20' />

								{/* Pass content */}
								<div className='w-full h-full p-4 flex flex-col gap-2'>
									{/* Event image */}
									<div
										className='relative overflow-hidden rounded-lg border-2 border-foreground/10 dark:border-white/10'
										style={{
											width: `${style.width - 32}px`,
											height: `${style.height - 80}px`,
										}}
									>
										<Image
											src={url}
											fill
											alt={`Event pass ${index + 1}`}
											className='object-cover'
											draggable={false}
										/>
									</div>

									{/* Pass details */}
									<div className='flex flex-col gap-1'>
										<div className='w-1/2 h-2 bg-foreground/10 dark:bg-white/20 rounded-full' />
										<div className='w-3/4 h-2 bg-foreground/5 dark:bg-white/10 rounded-full' />
									</div>
								</div>

								{/* Shine effect */}
								<div className='absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 bg-gradient-to-tr from-transparent via-white/10 to-transparent dark:via-white/5' />
							</div>
						);
					})}
				</DragElements>
			</section>
			{/* Why Should You Choose Us Section */}
			<section className='w-full min-h-screen bg-gradient-to-br from-background via-background to-blue-50/30 dark:to-blue-950/20 py-20 flex flex-col items-center justify-center relative overflow-hidden'>
				{/* Background decorative elements */}
				<div className='absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-2xl' />
				<div className='absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-full blur-2xl' />

				<h1 className='text-2xl md:text-6xl text-center text-foreground font-bold relative z-10'>
					Why Should You Choose{' '}
					<span className='bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent'>
						Us?
					</span>
				</h1>
				<p className='text-center text-muted-foreground text-sm md:text-base relative z-10'>
					Zero tolerance for scamming!
				</p>
				<div className='flex flex-col md:flex-row gap-0 items-center justify-center mt-10 px-10 md:px-0 relative z-10'>
					<Card
						variant='plus'
						{...cardContent}
						icon={<BookOpenCheck size={40} className='text-purple-600' />}
						className='max-w-[400px] bg-gradient-to-br from-background to-purple-50/50 dark:to-purple-950/20 h-[400px] border-purple-200/50 dark:border-purple-800/50'
					/>
					<Card
						variant='plus'
						{...cardContent1}
						className='max-w-[400px] bg-gradient-to-br from-background to-blue-50/50 dark:to-blue-950/20 h-[400px] border-blue-200/50 dark:border-blue-800/50'
						icon={<Zap size={40} className='text-blue-600' />}
					/>
					<Card
						variant='plus'
						{...cardContent2}
						className='max-w-[400px] bg-gradient-to-br from-background to-pink-50/50 dark:to-pink-950/20 h-[400px] border-pink-200/50 dark:border-pink-800/50'
						icon={<PartyPopper size={40} className='text-pink-600' />}
					/>
				</div>
				{/* Get Started Button */}
				<div className='flex items-center justify-center mt-10 relative z-10'>
					<Button
						variant='default'
						className='px-8 py-4 text-lg font-medium shadow-lg rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 transition-all duration-300 hover:scale-105'
						onClick={() => router.push('/events')}
					>
						Get Started
					</Button>
				</div>
			</section>
			{/* Testimonials Section */}
			<section className='w-full bg-gradient-to-br from-background via-background to-pink-50/30 dark:to-pink-950/20 py-20 flex flex-col items-center justify-center relative overflow-hidden'>
				{/* Background decorative elements */}
				<div className='absolute top-10 right-20 w-48 h-48 bg-gradient-to-br from-yellow-400/10 to-orange-400/10 rounded-full blur-3xl' />
				<div className='absolute bottom-10 left-20 w-36 h-36 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-full blur-3xl' />
				<div className='relative z-10'>
					<Testimonials />
				</div>
			</section>
			{/* Footer Section */}
			<Footer />
		</main>
	);
};

export default Landing;
